import { API_IDS } from '@intigral/constant';
import { DI } from '@intigral/constant/config/DIContainer';
import { IEntitlementRepository } from '../../core/repositories/interfaces/iEntitlementRepository';
import { container } from '../../DataDomain';
import { CSRFTokenRequest, IAmazonActivateResponse, IStatusSuccessResponse } from '../../entity';
import {
    IAllEntitlementsResponse,
    IAllowedCountriesResponse,
    IAmazonAccountRecoveryRequest,
    IAmazonAccountRecoveryResponse,
    IAmazonActivationLinkRequest,
    IAmazonActivationLinkResponse,
    IAvailablePackagesResponse,
    IChannelListResponse,
    IEntitlementPurchaseResponse,
    IGetSubscriptionsTiersResponse,
    INetflixForgotAccountPayload,
    INetflixForgotAccountResponse,
    IPaymentSwitchGeneratePinRequest,
    IPaymentSwitchVerifyPinRequest,
    IPPVPurchaseGenerateOtpPayload,
    IPPVPurchaseGenerateOtpResponse,
    IPPVPurchasePinPayload,
    IPPVPurchasePinResponse,
    IPPVPurchaseVerifyOtpPayload,
    IPPVPurchaseVerifyOtpResponse,
    IProfileMiniEpgChannelsParams,
    IQitafGenerateOtpPayload,
    IQitafOtpResponse,
    IQitafVerifyOtpPayload,
    ISubscribeEntitlementRequest,
    ITVODEntitlementsResponse,
    ITvodOtpResponse,
    ITvodOtpVerifyRequest,
    ITvodPricingTemplateResponse,
    TVODPurchaseRequest,
} from '../../entity/interface/iEntitlements';
import { IHttpAdapter } from '../../entity/interface/iHttpAdapter';
import { IApiPathManager } from '../config/ApiPathManager';
// import { subscriptionTierMock } from '../../subscriptionTierMock';

class EntitlementRepository implements IEntitlementRepository {
    // Lazy getters for dependencies to avoid circular dependencies
    private get apiPathManager(): IApiPathManager {
        return container.resolve<IApiPathManager>(DI.UTILS.IApiPathManager);
    }

    private get appKey(): string {
        return process.env['NX_PUBLIC_APP_KEY'] || 'APP_KEY';
    }

    private get http(): IHttpAdapter {
        return container.resolve<IHttpAdapter>(DI.UTILS.IHttpAdapter);
    }

    private get apiV2Http(): IHttpAdapter {
        return container.resolve<IHttpAdapter>(DI.UTILS.IHttpAdapter);
    }

    constructor() {
        // No initialization needed - dependencies resolved lazily via getters
    }

    private setAuthHeaders(ignore: boolean, token: string | undefined, identityToken: string | undefined): object {
        const headers: { [key: string]: string } = {};
        if (ignore) {
            return headers;
        }
        if (token) {
            headers['Authorization'] = token;
        }
        if (identityToken) {
            headers['identityToken'] = identityToken;
        }
        return headers;
    }

    async netflixForgotAccount(
        payload: INetflixForgotAccountPayload,
        csrfToken?: CSRFTokenRequest
    ): Promise<INetflixForgotAccountResponse> {
        const res = await this.apiV2Http.post<INetflixForgotAccountResponse>(
            this.apiPathManager.getPath(API_IDS.ENTITLEMENT_NETFLIX_FORGOT_ACCOUNT),
            {
                withCredentials: true,
                body: {
                    ...csrfToken,
                    ...payload,
                    apiKey: payload.userApiKey,
                    username: payload.username,
                    language: payload.language,
                    authToken: payload.authToken,
                    authSign: payload.authSign,
                    identityToken: payload.identityToken,
                },
            }
        );

        return res.data;
    }

    async qitafGenerateOtp(
        subType: 'NEW' | 'EXISTING',
        language: string,
        payload: string | IQitafGenerateOtpPayload,
        token?: string | undefined,
        identityToken?: string | undefined
    ): Promise<IQitafOtpResponse> {
        const apiEndPoint =
            typeof payload !== 'string' && payload?.['payPerViews'] && payload?.['payPerViews']?.length > 0
                ? API_IDS.SLP_QITAF_GENERATE_OTP
                : API_IDS.ENTITLEMENT_QITAF_GENERATE_OTP;
        const response = await this.http.post<IQitafOtpResponse>(this.apiPathManager.getPath(apiEndPoint), {
            params: {
                appKey: process.env['NX_PUBLIC_APP_KEY'],
            },
            headers: {
                ...this.setAuthHeaders(subType === 'NEW', token, identityToken),
                'Accept-Language': language,
            },
            body: typeof payload === 'string' ? JSON.parse(payload) : payload,
        });
        return response.data;
    }

    async qitafTvodGenerateOtp(
        productId: string,
        qitafMsisdn: string,
        uid: string,
        apiKey: string,
        token: string,
        identityToken: string
    ): Promise<any> {
        const response = await this.http.post<any>(
            this.apiPathManager.getPath(API_IDS.ENTITLEMENT_QITAF_TVOD_GENERATE_OTP),
            {
                params: {
                    apiKey,
                },
                headers: {
                    ...this.setAuthHeaders(false, token, identityToken),
                },
                body: {
                    productId,
                    qitafMsisdn,
                    ...(typeof uid === 'string' && uid.length > 0 ? { uid } : {}),
                },
            }
        );
        return response.data;
    }

    async qitafVerifyOtp(
        subType: 'NEW' | 'EXISTING',
        language: string,
        payload: string | IQitafVerifyOtpPayload,
        token?: string | undefined,
        identityToken?: string | undefined
    ): Promise<IQitafOtpResponse> {
        const apiEndPoint =
            typeof payload !== 'string' && payload?.['payPerViews'] && payload?.['payPerViews']?.length > 0
                ? API_IDS.SLP_QITAF_VERIFY_OTP
                : API_IDS.ENTITLEMENT_QITAF_VERIFY_OTP;

        const response = await this.http.post<IQitafOtpResponse>(this.apiPathManager.getPath(apiEndPoint), {
            params: {
                appKey: process.env['NX_PUBLIC_APP_KEY'],
            },
            headers: {
                ...this.setAuthHeaders(subType === 'NEW', token, identityToken),
                'Accept-Language': language,
            },
            body: typeof payload === 'string' ? JSON.parse(payload) : payload,
        });
        return response.data;
    }

    async qitafTvodVerifyOtp(
        productId: string,
        qitafMsisdn: string,
        qitafRefId: string,
        otp: number,
        amount: number,
        uid: string,
        apiKey: string,
        token: string,
        identityToken: string
    ): Promise<any> {
        const response = await this.http.post<any>(
            this.apiPathManager.getPath(API_IDS.ENTITLEMENT_QITAF_TVOD_VERIFY_OTP),
            {
                params: {
                    apiKey,
                },
                headers: {
                    ...this.setAuthHeaders(false, token, identityToken),
                },
                body: {
                    productId,
                    qitafMsisdn,
                    qitafRefId,
                    otp,
                    amount,
                    ...(typeof uid === 'string' && uid.length > 0 ? { uid } : {}),
                },
            }
        );
        return response;
    }

    async allowedCountries(apiKey: string): Promise<IAllowedCountriesResponse> {
        const response = await this.http.get<IAllowedCountriesResponse>(
            this.apiPathManager.getPath(API_IDS.ENTITLEMENT_ALLOWED_COUNTRIES),
            {
                params: {
                    apiKey: apiKey,
                },
            }
        );
        return response.data;
    }

    async availablePackages(
        apiKey: string,
        username: string,
        token: string
    ): Promise<IAvailablePackagesResponse | any> {
        const response = await this.http.get<IAvailablePackagesResponse>(
            this.apiPathManager.getPath(API_IDS.ENTITLEMENT_AVAILABLE_PACKAGES),
            {
                params: {
                    apiKey: apiKey,
                    username: username,
                },
                headers: {
                    Authorization: token,
                },
            }
        );
        return response.data;
    }

    async getTVods(apiKey: string, username: string, token: string): Promise<ITVODEntitlementsResponse | any> {
        const response = await this.http.get<ITVODEntitlementsResponse>(
            this.apiPathManager.getPath(API_IDS.ENTITLEMENT_TVODS),
            {
                params: {
                    apiKey: apiKey,
                    username: username,
                },
                headers: {
                    Authorization: token,
                },
            }
        );
        return response.data;
    }

    async getAll(apiKey: string, username: string, token: string): Promise<IAllEntitlementsResponse | any> {
        const response = await this.http.get<IAllEntitlementsResponse>(
            this.apiPathManager.getPath(API_IDS.ENTITLEMENT_ALL),
            {
                params: {
                    apiKey: apiKey,
                    username: username,
                },
                headers: {
                    Authorization: token,
                },
            }
        );
        return response.data;
    }

    async allowedChannels(
        apiKey: string,
        username: string,
        language: string,
        device: string,
        country: string,
        token: string,
        identityToken: string
    ): Promise<IChannelListResponse | any> {
        const response = await this.http.get<IChannelListResponse>(
            this.apiPathManager.getPath(API_IDS.ENTITLEMENT_CHANNELS),
            {
                params: {
                    apiKey: apiKey,
                    username: username,
                    device: device,
                    country: country,
                },
                headers: {
                    Authorization: token,
                    'Accept-Language': language,
                    identityToken: identityToken,
                },
            }
        );
        return response.data;
    }

    async getProfileMiniEpgChannels(
        apiKey: string,
        username: string,
        profile: string,
        query: IProfileMiniEpgChannelsParams,
        token: string
    ): Promise<any> {
        const response = await this.http.get<IChannelListResponse>(
            this.apiPathManager.getPath(API_IDS.ENTITLEMENT_PROFILE_EPG_CHANNELS),
            {
                params: {
                    apiKey: apiKey,
                    username: username,
                    profile: profile,
                    ...query,
                },
                headers: {
                    Authorization: token,
                },
            }
        );
        return response.data;
    }

    async subscribe(
        username: string,
        payload: ISubscribeEntitlementRequest,
        token: string
    ): Promise<IEntitlementPurchaseResponse> {
        const response = await this.http.post<IEntitlementPurchaseResponse>(
            this.apiPathManager.getPath(API_IDS.ENTITLEMENT_SUBSCRIBE),
            {
                params: {
                    appKey: this.appKey,
                    username: username,
                },
                headers: {
                    Authorization: token,
                },
                body: payload,
            }
        );
        return response.data;
    }

    async tVodPurchase(
        username: string,
        payload: TVODPurchaseRequest,
        token: string,
        identityToken: string
    ): Promise<IEntitlementPurchaseResponse> {
        const response = await this.http.post<IEntitlementPurchaseResponse>(
            this.apiPathManager.getPath(API_IDS.ENTITLEMENT_TVOD_PURCHASE),
            {
                params: {
                    userName: username,
                },
                headers: {
                    Authorization: token,
                    identityToken,
                },
                body: payload,
            }
        );
        return response.data;
    }

    async paymentSwitchGeneratePin(
        payload: IPaymentSwitchGeneratePinRequest,
        authToken: string,
        authSign: string,
        identityToken: string,
        csrfToken: CSRFTokenRequest
    ): Promise<IStatusSuccessResponse> {
        const response = await this.apiV2Http.post<IStatusSuccessResponse>(
            this.apiPathManager.getPath(API_IDS.ENTITLEMENT_PAYMENT_SWITCH_GENERATE_PIN),
            {
                withCredentials: true,
                body: {
                    ...csrfToken,
                    ...payload,
                    authToken,
                    authSign,
                    identityToken,
                },
            }
        );
        return response.data;
    }

    async paymentSwitchVerifyPin(
        payload: IPaymentSwitchVerifyPinRequest,
        authToken: string,
        authSign: string,
        identityToken: string,
        csrfToken: CSRFTokenRequest
    ): Promise<any> {
        const response = await this.apiV2Http.post<IStatusSuccessResponse>(
            this.apiPathManager.getPath(API_IDS.ENTITLEMENT_PAYMENT_SWITCH_VERIFY_PIN),
            {
                withCredentials: true,
                body: {
                    ...csrfToken,
                    ...payload,
                    authToken,
                    authSign,
                    identityToken,
                },
            }
        );
        return response.data;
    }

    async getAllSubscriptionTiers(
        apiKey: string,
        country: string,
        device: string,
        productKey: string,
        identityToken: string,
        groupBy?: string,
        showAllSubscriptions?: string,
        apiVersion?: string
    ): Promise<IGetSubscriptionsTiersResponse> {
        // // mock data should remove start
        // console.log(subscriptionTierMock, subscriptionTierMock);
        // return subscriptionTierMock;
        // // mock data should remove end
        const response = await this.http.get<IGetSubscriptionsTiersResponse>(
            this.apiPathManager.getPath(
                apiVersion && apiVersion?.toLowerCase() === 'v1'
                    ? API_IDS.ENTITLEMENT_ALL_SUBSCRIPTION_TIERS
                    : API_IDS.ENTITLEMENT_ALL_SUBSCRIPTION_TIERS_V2
            ),
            {
                params: {
                    apikey: apiKey,
                    country: country,
                    device: device,
                    productKey: productKey,
                    groupBy: groupBy,
                    showAllSubscriptions: showAllSubscriptions,
                },
                headers: {
                    identityToken: identityToken,
                },
            }
        );
        return response.data;
    }

    async getAmazonActivationLink(
        payload: IAmazonActivationLinkRequest,
        apiToken: string | { [key: string]: string }
    ): Promise<IAmazonActivationLinkResponse> {
        const response = await this.apiV2Http.post<IAmazonActivateResponse>(
            this.apiPathManager.getPath(API_IDS.ENTITLEMENT_AMAZON_ACTIVTION_LINK),
            {
                withCredentials: true,
                body: {
                    ...payload,
                    ...(typeof apiToken === 'object' ? { ...apiToken } : {}),
                },
            }
        );
        return response.data;
    }

    async amazonAccountRecovery(
        payload: IAmazonAccountRecoveryRequest,
        apiToken: string | { [key: string]: string }
    ): Promise<IAmazonAccountRecoveryResponse> {
        const response = await this.apiV2Http.post<IAmazonAccountRecoveryResponse>(
            this.apiPathManager.getPath(API_IDS.ENTITLEMENT_AMAZON_ACCOUNT_RECOVERY),
            {
                withCredentials: true,
                body: {
                    ...payload,
                    ...(typeof apiToken === 'object' ? { ...apiToken } : {}),
                },
            }
        );
        return response.data;
    }

    async getTvodPricingTemplates(operator: string): Promise<ITvodPricingTemplateResponse> {
        const response = await this.http.get<ITvodPricingTemplateResponse>(
            this.apiPathManager.getPath(API_IDS.ENTITLEMENT_TVOD_PRICING_TEMPLATE),
            {
                params: {
                    operator,
                },
            }
        );
        return response.data;
    }

    async tvodOtpGenerate(
        payload: any,
        username: string,
        token: string,
        identityToken: string
    ): Promise<ITvodOtpResponse> {
        const response = await this.http.post<ITvodOtpResponse>(
            this.apiPathManager.getPath(API_IDS.ENTITLEMENT_TVOD_OTP_GENERATE),
            {
                params: {
                    appKey: this.appKey,
                    userName: username,
                },
                headers: {
                    Authorization: token,
                    identityToken,
                },
                body: payload,
            }
        );

        return response.data;
    }

    async tvodOtpVerify(
        payload: ITvodOtpVerifyRequest,
        username: string,
        token: string,
        identityToken: string
    ): Promise<ITvodOtpResponse> {
        const response = await this.http.put<ITvodOtpResponse>(
            this.apiPathManager.getPath(API_IDS.ENTITLEMENT_TVOD_OTP_VERIFY),
            {
                params: {
                    appKey: this.appKey,
                    userName: username,
                },
                headers: {
                    Authorization: token,
                    identityToken,
                },
                body: payload,
            }
        );

        return response.data;
    }
    async ppvPurchaseGenerateOtp(
        payload: IPPVPurchaseGenerateOtpPayload,
        username: string,
        authToken: string,
        authSign: string,
        identityToken: string,
        csrfToken: { [key: string]: string },
        apiKey: string
    ): Promise<IPPVPurchaseGenerateOtpResponse> {
        const response = await this.http.post<IPPVPurchaseGenerateOtpResponse>(
            this.apiPathManager.getPath(API_IDS.PPV_PURCHASE_GENERATE_PIN),
            {
                withCredentials: true,
                params: {
                    apiKey,
                    username,
                },
                headers: {
                    ...this.setAuthHeaders(false, authToken, identityToken),
                },
                body: {
                    ...payload,
                    username,
                    authToken,
                    authSign,
                    identityToken,
                    ...csrfToken,
                },
            }
        );
        return response.data;
    }

    async ppvPurchaseVerifyOtp(
        payload: IPPVPurchaseVerifyOtpPayload,
        username: string,
        authToken: string,
        authSign: string,
        identityToken: string,
        csrfToken: { [key: string]: string },
        apiKey: string
    ): Promise<IPPVPurchaseVerifyOtpResponse> {
        const response = await this.http.post<IPPVPurchaseVerifyOtpResponse>(
            this.apiPathManager.getPath(API_IDS.PPV_PURCHASE_VERIFY_PIN),
            {
                withCredentials: true,
                params: {
                    apiKey,
                    username,
                },
                headers: {
                    ...this.setAuthHeaders(false, authToken, identityToken),
                },
                body: {
                    ...payload,
                    username,
                    authToken,
                    authSign,
                    identityToken,
                    ...csrfToken,
                },
            }
        );
        return response.data;
    }

    async ppvPurchasePin(
        payload: IPPVPurchasePinPayload,
        username: string,
        authToken: string,
        authSign: string,
        identityToken: string,
        csrfToken: { [key: string]: string },
        apiKey: string
    ): Promise<IPPVPurchasePinResponse> {
        const response = await this.http.post<IPPVPurchasePinResponse>(
            this.apiPathManager.getPath(API_IDS.PPV_PURCHASE_PIN),
            {
                withCredentials: true,
                params: {
                    apikey: apiKey,
                    username,
                },
                headers: {
                    ...this.setAuthHeaders(false, authToken, identityToken),
                },
                body: {
                    ...payload,
                    username,
                    authToken,
                    authSign,
                    identityToken,
                    ...csrfToken,
                },
            }
        );
        return response.data;
    }
}

export default EntitlementRepository;
